import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { NAV_GROUPS, pageForPath } from './nav';

/** Masaüstü üst menü: Ana Sayfa + başlık başına açılır liste. Seçilen sayfa tek başına açılır. */
export function TopNav() {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const page = pageForPath(pathname);
    return (
        <nav className="hidden md:flex items-center gap-0.5" data-testid="top-nav" aria-label="Ana menü">
            {NAV_GROUPS.map((g) => {
                const Icon = g.icon;
                const active = page.group?.key === g.key;
                const cls = `inline-flex items-center gap-1.5 h-8 px-2.5 rounded-sm text-xs font-medium transition-colors ${
                    active ? 'text-emerald-200 bg-emerald-500/10 border border-emerald-500/30' : 'text-slate-300 hover:text-slate-50 hover:bg-slate-800 border border-transparent'
                }`;
                if (!g.items) {
                    return (
                        <button key={g.key} onClick={() => navigate(g.path)} className={cls} data-testid="nav-home" aria-current={active ? 'page' : undefined}>
                            <Icon className="h-3.5 w-3.5" /> {g.label}
                        </button>
                    );
                }
                return (
                    <DropdownMenu key={g.key}>
                        <DropdownMenuTrigger asChild>
                            <button className={cls} data-testid={`nav-group-${g.key}`} aria-current={active ? 'page' : undefined}>
                                <Icon className="h-3.5 w-3.5" /> {g.label} <ChevronDown className="h-3 w-3 opacity-70" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="bg-slate-800 border-slate-700 text-slate-100 w-80 p-1.5">
                            {g.items.map((it) => (
                                <DropdownMenuItem
                                    key={it.path}
                                    onSelect={() => navigate(it.path)}
                                    data-testid={`nav-item-${it.path.slice(1)}`}
                                    className={`flex items-start gap-2.5 rounded-md px-2.5 py-2 cursor-pointer focus:bg-slate-700 ${pathname === it.path ? 'bg-emerald-500/10' : ''}`}
                                >
                                    <span className="text-base leading-none mt-0.5">{it.emoji}</span>
                                    <span className="min-w-0">
                                        <span className="block text-xs font-semibold text-slate-50">{it.label}</span>
                                        <span className="block text-[11px] text-slate-400 leading-snug">{it.desc}</span>
                                    </span>
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            })}
        </nav>
    );
}

/** Mobil: hamburger → tüm sayfalar başlıklarına göre bir yan panelde. */
export function MobileMenu({ open, onOpenChange }) {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const go = (path) => { onOpenChange(false); navigate(path); window.scrollTo({ top: 0 }); };
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="left" className="bg-slate-900 border-slate-700 text-slate-100 w-[86vw] max-w-sm overflow-y-auto p-0" data-testid="mobile-menu-sheet">
                <SheetHeader className="px-4 pt-4 pb-2 text-left">
                    <SheetTitle className="text-slate-50 text-sm tracking-tight">Menü</SheetTitle>
                </SheetHeader>
                <div className="px-2 pb-6 space-y-3">
                    {NAV_GROUPS.map((g) => {
                        const Icon = g.icon;
                        const items = g.items || [{ path: g.path, label: g.label, emoji: '🏠', desc: 'Özet ve hızlı erişim' }];
                        return (
                            <div key={g.key}>
                                <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-slate-400">
                                    <Icon className="h-3 w-3" /> {g.label}
                                </div>
                                <ul className="space-y-0.5">
                                    {items.map((it) => (
                                        <li key={it.path}>
                                            <button
                                                onClick={() => go(it.path)}
                                                data-testid={`mobile-menu-item-${it.path === '/' ? 'home' : it.path.slice(1)}`}
                                                className={`w-full flex items-start gap-2.5 rounded-lg px-3 py-2 text-left ${pathname === it.path ? 'bg-emerald-500/10 border border-emerald-500/30' : 'hover:bg-slate-800 border border-transparent'}`}
                                            >
                                                <span className="text-base leading-none mt-0.5">{it.emoji}</span>
                                                <span className="min-w-0">
                                                    <span className="block text-xs font-semibold text-slate-50">{it.label}</span>
                                                    <span className="block text-[11px] text-slate-400 leading-snug">{it.desc}</span>
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            </SheetContent>
        </Sheet>
    );
}

export function MobileMenuButton({ onClick }) {
    return (
        <button onClick={onClick} data-testid="mobile-menu-button" aria-label="Menü"
                className="md:hidden h-8 w-8 inline-flex items-center justify-center rounded-sm border border-slate-700 bg-slate-800 text-slate-200">
            <Menu className="h-4 w-4" />
        </button>
    );
}

export function useMobileMenu() {
    const [open, setOpen] = useState(false);
    return { open, setOpen };
}
